import type {
  ChatMessage,
  ToolCallProgress,
} from "schemas/generated/chatMessage";

import React, { createContext } from "react";

import { useShallowStore } from "replo-utils/react/zustand";
import { createStore } from "zustand";

interface StreamingState {
  isStreaming: boolean;
  controller?: AbortController;
}

interface ChatStore {
  // NOTE (Bobby, 2025-09-11): All these map chatId to a specific chat's state. Tool call progress may not need to be mapped to a chatId
  // but worth keeping it mapped to chatId since we don't really know how unique toolCallIds are as generated by the LLM providers. Don't want
  // to encounter a weird state where there's a collision of toolCallIds across chats so it's easier to just map it to a chat as well.
  streamingStates: Record<string, StreamingState>;
  chatMessages: Record<string, ChatMessage[]>;
  chatToolCallProgress: Record<string, Record<string, ToolCallProgress>>;
  // NOTE (Max, 2025-11-26): For the active chat, we store the active chat turn id (initiated when streaming begins, on the frontend)
  chatTurnIds: Record<string, string>;
  // NOTE (Yuxin, 2026-01-25): Maps chatId to agentSessionId for answering questions
  chatIdToAgentSessionId: Record<string, string>;
  // NOTE (Yuxin, 2026-02-04): Question ID cache for question tool. Maps question tool call id to question id
  questionIds: Record<string, string>;

  actions: {
    setStreamingState: (chatId: string, state: StreamingState) => void;
    getStreamingState: (chatId: string) => StreamingState;
    getAgentSessionId: (chatId: string) => string | undefined;
    // NOTE (Yuxin, 2026-02-04): Question ID cache for question tool
    // Maps toolCallId to questionId for answering questions via the question API
    setQuestionId: (toolCallId: string, questionId: string) => void;
  };
}

const createChatStore = () => {
  return createStore<ChatStore>((set, get) => ({
    streamingStates: {},
    chatMessages: {},
    chatToolCallProgress: {},
    chatTurnIds: {},
    chatIdToAgentSessionId: {},
    questionIds: {},
    actions: {
      setStreamingState: (chatId: string, streamingState: StreamingState) => {
        return set((state) => ({
          streamingStates: {
            ...state.streamingStates,
            [chatId]: streamingState,
          },
        }));
      },

      getStreamingState: (chatId: string) => {
        return get().streamingStates[chatId] ?? { isStreaming: false };
      },

      getAgentSessionId: (chatId: string) => {
        return get().chatIdToAgentSessionId[chatId];
      },

      setQuestionId: (toolCallId: string, questionId: string) => {
        return set((state) => ({
          questionIds: {
            ...state.questionIds,
            [toolCallId]: questionId,
          },
        }));
      },
    },
  }));
};

type ChatContextType = {
  store: ReturnType<typeof createChatStore>;
};

const ChatContext = createContext<ChatContextType | null>(null);

const useChatContext = () => {
  const context = React.useContext(ChatContext);
  if (!context) {
    // NOTE (Yuxin, 2026-01-30): This error means AgentChatStoreProvider is missing.
    // If you see this after making changes, try a hard refresh (Cmd+Shift+R)
    console.error(
      "[ChatStoreContext] AgentChatStoreProvider not found. Try hard refresh.",
    );
    // eslint-disable-next-line replo/no-raw-error-throw
    throw new Error(
      "AgentChatStoreProvider must be rendered before using chat hooks. " +
        "This can happen if the browser is using cached code - try hard refresh (Cmd+Shift+R)",
    );
  }
  return context;
};

export function ChatProvider({ children }: React.PropsWithChildren) {
  const [store] = React.useState(() => createChatStore());

  const contextValue = React.useMemo(
    () => ({
      store,
    }),
    [store],
  );

  return (
    <ChatContext.Provider value={contextValue}>{children}</ChatContext.Provider>
  );
}

function useChatStore<SelectorReturnType>(
  selector: (state: ChatStore) => SelectorReturnType,
): SelectorReturnType {
  const { store } = useChatContext();
  return useShallowStore(store, selector);
}

export const useStreamingChats = () => {
  return useChatStore((state) => {
    return Object.entries(state.streamingStates)
      .filter(([, streamingState]) => streamingState.isStreaming)
      .map(([chatId]) => chatId);
  });
};
